/* eslint-disable max-len */
/* eslint-disable no-warning-comments */
/**
 * A component to display page header on each Wizard page
 *
 * Imported from v9 root.html <header />
 */
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import { WizardContext } from '@xengage/gw-portals-wizard-react';
import appConfig from 'app-config';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { MLPolicyTypeUtil } from 'ml-portals-util-react';
  
import styles from './CustomQuoteInfoComponent.module.scss';
import messages from './CustomQuoteInfoComponent.messages';
  
const CustomQuoteInfoComponent = (props) => {
    const { submissionVM, additionalContent } = props;
  
    const wizardContext = useContext(WizardContext);
    const translator = useTranslator();
    const quoteId = _.get(submissionVM, 'quoteID.value');
    const isGPA = appConfig.portalCode === 'GPA';
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isLLCorLLH = isGPA && (submissionVM?.value?.isLandlordCondo() || submissionVM?.value?.isLandlordHome());
  
    const {
        getPolicyTypeName
    } = MLPolicyTypeUtil();
  
    const { currentStep } = wizardContext;
  
    const isOnPageThatDisplaysQuoteNumber = useCallback(() => {
        const pagesWithoutQuoteNumber = ['PAQuotePage', 'HOQuotePage', 'PUPQuotePage', 'PASummaryPage', 'HOSummaryPage', 'confirmation', 'PUPSummaryPage', 'HOSummaryPage'];
        return pagesWithoutQuoteNumber.indexOf(currentStep.id) === -1;
    }, [currentStep]);
  
    const isQuoteNumberPopulated = useCallback(() => {
        return !!quoteId;
    }, [quoteId]);
  
    const displayQuoteNumber = useCallback(() => {
        let shouldQuoteNumberDisplay = false;
        if (isQuoteNumberPopulated() && isOnPageThatDisplaysQuoteNumber()) {
            shouldQuoteNumberDisplay = true;
        }
        return shouldQuoteNumberDisplay;
    }, [isQuoteNumberPopulated, isOnPageThatDisplaysQuoteNumber]);
  
    const isBundle = useCallback(() => {
        return submissionVM.value && submissionVM.value.isBundle_ML;
    }, [submissionVM]);
  
    const getLobLabel = useCallback(() => {
        let lobLabel = '';
        if (isBundle()) {
            if (submissionVM.baseData) {
                const productCode = submissionVM.value.baseData.productCode;
                if (MLProductCodeUtil.isHomeOwners(productCode)) {
                    const coveragePartType = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', undefined);
                    lobLabel = getPolicyTypeName(coveragePartType);
                } else {
                    lobLabel = getPolicyTypeName(productCode);
                }
            }
        }
  
        return lobLabel;
    }, [getPolicyTypeName, isBundle, submissionVM]);
  
    const getPageTitle = useCallback(() => {
        const productCode = _.get(submissionVM, 'value.baseData.productCode');
        if (!isGPA && productCode && MLProductCodeUtil.isHomeOwners(productCode) && currentStep.mlStepName === 'quoteDetails') {
            const coveragePartType = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', undefined);
            const lobLabel = _.lowerCase(getPolicyTypeName(coveragePartType));
            return translator(messages.yourInsuranceQuote, { coverageType: lobLabel });
        }
        if(isModernProduct && currentStep.mlStepName === 'compHoValidateInfo' && isLLCorLLH){
            return translator(messages.mlHeaderForLLCandLLH)
        }
        return currentStep.mlHeader && translator(currentStep.mlHeader);
    }, [currentStep.mlHeader, currentStep.mlStepName, getPolicyTypeName, isGPA, submissionVM, translator]);
  
    const getPageSubTitle = useCallback(() => {
        // TODO add mlSubtitle to wizardStep
        return null;
    }, []);
  
    const showPageTitle = useCallback(() => {
        return typeof getPageTitle() !== 'undefined' && getPageTitle().length !== 0;
    }, [getPageTitle]);
  
    if (!displayQuoteNumber() && !showPageTitle()) {
        return null;
    }
  
    if (!isGPA) {
        return (
            <header className={isGPA ? styles['gpa-quote-info'] : styles['quote-info']}>
                {displayQuoteNumber()
                      && (
                          <div className={styles['quote-number']}>
                              <b>
                                  {getLobLabel()
                                      && (<span>{getLobLabel()}&nbsp;</span>)
                                  }
                                  {
                                      translator(messages.quoteNumber)
                                  }
                              </b>
                              {quoteId}
                          </div>
                      )}
                {
                    showPageTitle()
                      && <h1 className="page-header headerOne">{getPageTitle()}</h1>
                }
                {
                    getPageSubTitle()
                      && <h2 className="page-subheader">{getPageSubTitle()}</h2>
                }
            </header>
        );
    }
  
    /**
       * For GPA, if the page doesn't have the property 'mlHeader' in the wizard config file means one thing:
       * The page has it's own header (like PA's Drivers Page, Vehicles Page, Quote Page, ...).
       * In those cases the context won't render the header.
       * This also means that we can't draw the Alerts Component (it would render on top on the page's header),
       * so the page will need to have its own Alerts Component.
       * The Alert Component doesn't need any parameters, see below for an example. 
       */
    if (showPageTitle()) {
        return (
            <React.Fragment>
                <header className={`ml-main-header ${styles.header}`}>
                    <h1 className="page-header">{getPageTitle()}</h1>
                    {additionalContent}
                </header>
                <MLAlertGroupComponent />
            </React.Fragment>
        )
    }
    return <React.Fragment />
};
  
CustomQuoteInfoComponent.propTypes = {
    submissionVM: PropTypes.shape({
        value: PropTypes.shape({
            isBundle_ML: PropTypes.bool
        })
    }).isRequired,
    additionalContent: PropTypes.node
};
  
CustomQuoteInfoComponent.defaultProps = {
    additionalContent: null
}
  
export default CustomQuoteInfoComponent;